import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import {useState, useEffect, useContext} from 'react';
import styled from 'styled-components';
import {useNavigate} from 'react-router-dom';
import {EmailLinkform} from './EmailLinkForm';
import {EmailPasswordForm} from './EmailPasswordForm';
import {AlternateAuth} from './AlternateAuth';
import {LoginProvider, LoginContext, LoginScreen} from './LoginContext';
import {EmailSentNotice} from './EmailSentNotice';
import {
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
} from 'firebase/auth';
import {SignInButtonFlat} from './SignInButton';
import {OrSeparator} from './OrSeparator';
import Swal from 'sweetalert2';
import {StanfordLogo as StanfordLogoBase} from 'branding/StanfordLogo';
import {Loading} from 'components/loading/Loading';
import {useCourseId} from 'hooks/router/useUrlParams';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import {useSetPageTitle} from 'contexts/accessibility/AccessibilityContext';
import {AUTH_TEST_IDS} from './authTestIds';
import {carryReferralCode} from '@cip/core/application/referrals';
import {isBrowserOffline} from 'utils/connectionMessages';
import {errorCodeOf, signInFailureAdvice} from './emailSendError';

const FORGOT_PASSWORD_TEXT =
  'Enter the email address associated with your Code in Place account. We will send you further instructions for resetting your password.';
const EMAIL_SIGN_IN_TEXT =
  'Enter the email address associated with your Code in Place account. We will send you a link to sign in.';

const handleEmailLinkSignIn = async (
  auth,
  magicLinkEmail,
  actionCodeSettings,
) => {
  await sendSignInLinkToEmail(auth, magicLinkEmail, actionCodeSettings);
  window.localStorage.setItem('emailForSignIn', magicLinkEmail);
};

/** What the sign-in screens below need from {@link Login}. */
interface LoginScreenProps {
  /** The Firebase (compat) auth instance the forms sign in with. */
  auth: ReturnType<typeof firebase.auth>;
  /** Whether the email form signs in (true) or creates an account (false). */
  showSignIn: boolean;
  /** Switches the email form between signing in and creating an account. */
  setShowSignIn: (showSignIn: boolean) => void;
}

// These screens must stay at module scope. Defined inside Login, every Login
// re-render (an Android keyboard opening resizes the window) gave them a new
// component type, rebuilt the form, and dropped the focused input (#5319).

/** The first screen: Google/Canvas/one-time link, or the email form. */
function InitialLanding({
  auth,
  setShowSignIn,
}: Pick<LoginScreenProps, 'auth' | 'setShowSignIn'>) {
  const [, setLoginState] = useContext(LoginContext);
  const courseId = useCourseId();
  const t = useTranslateFunction('miscUI');
  // TODO:  see github issue # 966
  const isCS105 = courseId === 'cs105f24';
  return (
    <div className="d-flex flex-column mt-3">
      <AlternateAuth auth={auth} />
      {!isCS105 && <OrSeparator />}
      {!isCS105 && (
        <SignInButtonFlat
          className="mb-2 text-white"
          variant="secondary"
          data-testid={AUTH_TEST_IDS.createAccountLandingButton}
          onClick={() => {
            setShowSignIn(false);
            setLoginState(LoginScreen.LOGIN_FORM);
          }}
        >
          {t('Create an Account')}
        </SignInButtonFlat>
      )}
      {!isCS105 && (
        <SignInButtonFlat
          className="mb-2 text-white"
          variant="secondary"
          data-testid={AUTH_TEST_IDS.existingAccountLandingButton}
          onClick={() => {
            setShowSignIn(true);
            setLoginState(LoginScreen.LOGIN_FORM);
          }}
        >
          {t('I Have an Account')}
        </SignInButtonFlat>
      )}
    </div>
  );
}

/** The email and password form, with the sign-in / create-account switch. */
function LoginForm({auth, showSignIn, setShowSignIn}: LoginScreenProps) {
  const t = useTranslateFunction('miscUI');
  return (
    <div>
      <div className="d-flex flex-column">
        <EmailPasswordForm auth={auth} showSignIn={showSignIn} />
        <OrSeparator />
        <AlternateAuth auth={auth} btnVariant="outline-secondary" />
      </div>
      <div style={{maxWidth: '500px'}} className="text-center">
        <SwitchText>
          {showSignIn
            ? t('New to Code In Place?')
            : t('Already have an account?')}
        </SwitchText>
        <SignInButtonFlat
          style={{width: '100%'}}
          variant="light"
          data-testid={AUTH_TEST_IDS.modeSwitchButton}
          onClick={() => setShowSignIn(!showSignIn)}
        >
          {showSignIn ? t('Create Account') : t('Sign In')}
        </SignInButtonFlat>
      </div>
    </div>
  );
}

/** The current sign-in screen, chosen by {@link LoginContext}. */
function LoginBody({
  auth,
  showSignIn,
  setShowSignIn,
  actionCodeSettings,
}: LoginScreenProps & {
  /** Where emailed links return to; see `Login`. */
  actionCodeSettings: {url: string; handleCodeInApp: boolean};
}) {
  const [loginState] = useContext(LoginContext);
  const user = auth.currentUser;

  switch (loginState) {
    case LoginScreen.INIT_LANDING:
      return <InitialLanding auth={auth} setShowSignIn={setShowSignIn} />;
    case LoginScreen.UNVERIFIED:
      return (
        <EmailSentNotice
          emailHandler={sendEmailVerification}
          handlerArgs={[user, actionCodeSettings]}
        />
      );
    case LoginScreen.LOGIN_FORM:
      return (
        <div>
          <div className="mt-3">
            <LoginForm
              auth={auth}
              showSignIn={showSignIn}
              setShowSignIn={setShowSignIn}
            />
          </div>
        </div>
      );
    case LoginScreen.FORGOT_PASWORD:
      return (
        <EmailLinkform
          auth={auth}
          emailHandler={sendPasswordResetEmail}
          actionCodeSettings={actionCodeSettings}
          action="passwordReset"
          title={'Reset Password'}
          body={FORGOT_PASSWORD_TEXT}
        />
      );
    case LoginScreen.EMAIL_LINK:
      return (
        <EmailLinkform
          auth={auth}
          emailHandler={handleEmailLinkSignIn}
          actionCodeSettings={actionCodeSettings}
          action="signInLink"
          title={'One-time link'}
          body={EMAIL_SIGN_IN_TEXT}
        />
      );
  }
}

export const Login = () => {
  const [showSignIn, setShowSignIn] = useState(true);
  const setPageTitle = useSetPageTitle();
  useEffect(() => {
    setPageTitle('Sign In');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);
  // Emailed links often open in a different browser from the one the visitor
  // started in (e.g. out of an Instagram in-app browser), so the URL is the only
  // way a referral code survives signing in.
  const url = carryReferralCode({
    link: window.location.origin + window.location.pathname,
    search: window.location.search,
  });
  const urlWithoutSignInLinkParams = () =>
    carryReferralCode({
      link: location.href.split('?')[0],
      search: location.search,
    });
  const auth = firebase.auth();
  const user = auth.currentUser;
  const navigate = useNavigate();
  const actionCodeSettings = {
    url: url,
    handleCodeInApp: true,
  };
  const t = useTranslateFunction('miscUI');

  const connectionAdviceFor = (error: unknown): string | undefined => {
    const advice = signInFailureAdvice({
      code: errorCodeOf(error),
      isOffline: isBrowserOffline(),
    });
    return advice === undefined ? undefined : t(advice, {forceString: true});
  };

  const showEmailAlert = async () => {
    try {
      const {value: email} = await Swal.fire({
        title: 'Input email address',
        input: 'email',
        inputLabel: 'Your email address',
        inputPlaceholder: 'Enter your email address',
      });
      await signInWithEmailLink(auth, email, window.location.href);
      window.localStorage.removeItem('emailForSignIn');
      history.pushState(null, '', urlWithoutSignInLinkParams());
    } catch (error) {
      const advice = connectionAdviceFor(error);
      Swal.fire({
        icon: 'error',
        title: advice === undefined ? 'Oops...' : undefined,
        text: advice ?? error.message,
      });
    }
  };

  useEffect(() => {
    if (user) {
      navigate(
        carryReferralCode({link: location.pathname, search: location.search}),
      );
    } else if (isSignInWithEmailLink(auth, window.location.href)) {
      // eslint-disable-next-line prefer-const -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        showEmailAlert();
      } else {
        signInWithEmailLink(auth, email, window.location.href)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
          .then(result => {
            window.localStorage.removeItem('emailForSignIn');
            history.pushState(null, '', urlWithoutSignInLinkParams());
          })
          .catch(error => {
            window.alert(connectionAdviceFor(error) ?? error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  }, []);

  const {loginPageImageUrl, loginPageTitle, isLoading} = useCourseTheme();
  if (isLoading) {
    return <Loading />;
  }
  return (
    <OuterWrapper>
      {location.href.includes('apiKey') ? (
        <Loading />
      ) : (
        <LoginWrapper className="d-flex flex-column align-items-center">
          {!!loginPageImageUrl ? (
            <CourseLogo
              src={loginPageImageUrl}
              alt={`${loginPageTitle} ${t('Logo')}`}
            />
          ) : (
            <StanfordLogo alt="Stanford Logo" />
          )}
          <h1>{loginPageTitle ?? t('Code in Place')}</h1>
          <LoginProvider>
            <LoginBodyOuter>
              <LoginBody
                auth={auth}
                showSignIn={showSignIn}
                setShowSignIn={setShowSignIn}
                actionCodeSettings={actionCodeSettings}
              />
            </LoginBodyOuter>
          </LoginProvider>
        </LoginWrapper>
      )}
    </OuterWrapper>
  );
};

export const GenericBrandedWrapper = ({children}) => {
  const t = useTranslateFunction('miscUI');
  return (
    <OuterWrapper>
      <LoginWrapper className="d-flex flex-column align-items-center">
        <StanfordLogo alt="Stanford Logo" />
        <h1>{t('Code in Place')}</h1>
        {children}
      </LoginWrapper>
    </OuterWrapper>
  );
};

const LoginWrapper = styled.div`
  width: 100%;
  max-width: 300px;
  margin-bottom: 40px;
`;

const OuterWrapper = styled.div`
  display: flex;
  justify-content: center;
  height: 100vh;
`;

const StanfordLogo = styled(StanfordLogoBase)`
  background-size: contain;
  background-repeat: no-repeat;
  height: 100px;
  flex-grow: 1;
  max-height: 300px;
`;

const CourseLogo = styled.img`
  background-size: contain;
  background-repeat: no-repeat;
  object-fit: contain;
  flex-grow: 1;
  max-height: 250px;
  max-width: 400px;
  width: 100%;
`;

const LoginBodyOuter = styled.div``;

const SwitchText = styled.div`
  text-align: center;
  margin-top: 20px;
`;
